(() => {
  const previewRequests = new WeakMap();
  const showProductImage = (section, { url, alt }) => {
    const carousel = typeof window !== 'undefined' && window.AgetaProductGallery?.get(section);
    if (carousel) return carousel.show({ url, alt });
    const image = section.querySelector('[data-combination-main-image]');
    if (!image || !url) return Promise.resolve();
    image.src = url;
    image.srcset = '';
    image.alt = alt || '';
    if (typeof image.decode !== 'function') return Promise.resolve();

    const container = image.closest('.product-gallery__main');
    let status = container.querySelector('[data-gallery-load-status]');
    if (!status) {
      status = document.createElement('p');
      status.className = 'combination-gallery__load-status';
      status.dataset.galleryLoadStatus = '';
      status.setAttribute('role', 'status');
      container.append(status);
    }
    const request = {};
    previewRequests.set(section, request);
    container.setAttribute('aria-busy', 'true');
    status.textContent = '画像を読み込み中…';
    status.hidden = false;
    const isCurrent = () => previewRequests.get(section) === request
      && section.querySelector('[data-combination-main-image]') === image;
    return image.decode().then(() => {
      if (!isCurrent()) return;
      container.setAttribute('aria-busy', 'false');
      status.hidden = true;
    }, () => {
      if (!isCurrent()) return;
      container.setAttribute('aria-busy', 'false');
      status.textContent = '画像を読み込めませんでした。下の画像をもう一度選んでください。';
      status.hidden = false;
    });
  };

  // Retain decoded gallery images so repeated selections can use the browser cache.
  const preloadedImages = new Map();
  const preloadGallery = (section) => {
    if (typeof Image === 'undefined' || window.AgetaProductGallery?.get(section)) return;
    section.querySelectorAll('[data-combination-image-thumb]').forEach((thumb) => {
      const url = thumb.dataset.imageUrl;
      if (!url || preloadedImages.has(url)) return;
      const image = new Image();
      image.decoding = 'async';
      image.fetchPriority = 'low';
      preloadedImages.set(url, image);
      image.onerror = () => preloadedImages.delete(url);
      image.src = url;
      image.decode?.().catch(() => {});
    });
  };

  const findVariant = (variants, selections) => variants.find((variant) => (
    Object.entries(selections).every(([position, value]) => variant.options[Number(position) - 1] === value)
  ));

  const filterCharacterDetails = (details, characterValue) => {
    const selectedValue = String(characterValue || '').trim();
    if (!selectedValue) return [];
    return details.filter((detail) => detail.characterValue === selectedValue);
  };

  const hierarchicalSelectionComplete = (selection) => ['character', 'detail', 'phrase'].every((key) => (
    typeof selection[key] === 'string' && selection[key].trim() !== ''
  ));

  const isSelectControl = (control) => control?.tagName?.toLowerCase() === 'select';

  const selectedOption = (control) => {
    if (!isSelectControl(control)) return control;
    return control.selectedOptions?.[0] || control.options?.[control.selectedIndex];
  };

  const selectedControlValue = (control) => {
    if (!control || control.disabled) return '';
    if (isSelectControl(control)) return String(control.value || '').trim();
    return control.checked ? String(control.value || '').trim() : '';
  };

  const hasSelectableOptions = (controls) => controls.some((control) => {
    if (!control) return false;
    if (isSelectControl(control)) {
      return [...(control.options || [])].some((option) => String(option.value || '').trim() !== '');
    }
    return !control.disabled && String(control.value || '').trim() !== '';
  });

  const getClearedControlState = (control) => (
    isSelectControl(control) ? { value: '' } : { checked: false }
  );

  const clearControl = (control) => {
    if (!control) return;
    Object.assign(control, getClearedControlState(control));
  };

  const readSelection = (control) => {
    const option = selectedOption(control);
    const value = selectedControlValue(control);
    return {
      value,
      id: value ? (option?.dataset?.selectionId || '') : '',
      label: value ? (option?.dataset?.selectionLabel || value) : '',
    };
  };

  const formatMoney = (priceInCents, currency, locale = 'ja-JP') => {
    const amount = Number(priceInCents) / 100;
    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
        currencyDisplay: 'narrowSymbol',
      }).format(amount);
    } catch {
      return `¥${Math.round(amount).toLocaleString('ja-JP')}`;
    }
  };

  const variantImage = (variant, fallbackUrl, fallbackAlt) => ({
    url: variant?.imageUrl || fallbackUrl || '',
    alt: variant?.imageAlt || fallbackAlt || '',
  });

  const parseVariants = (select) => [...select.options].map((option) => ({
    element: option,
    id: option.value,
    available: option.dataset.available === 'true',
    price: Number(option.dataset.price),
    priceFormatted: option.dataset.priceFormatted || '',
    comparePriceFormatted: option.dataset.comparePriceFormatted || '',
    imageUrl: option.dataset.imageUrl || '',
    imageAlt: option.dataset.imageAlt || '',
    options: [option.dataset.option1 || '', option.dataset.option2 || '', option.dataset.option3 || ''],
  }));

  const initVariantProduct = (section) => {
    if (section.dataset.combinationBound === 'true') return;

    const variantSelect = section.querySelector('[data-combination-variant-select]');
    const optionInputs = [...section.querySelectorAll('[data-combination-option]')];
    const quantity = section.querySelector('[data-combination-quantity]');
    const submit = section.querySelector('[data-combination-submit]');
    if (!variantSelect || optionInputs.length === 0 || !quantity || !submit) return;

    section.dataset.combinationBound = 'true';
    const variants = parseVariants(variantSelect);
    const currency = section.dataset.currency || 'JPY';
    const locale = document.documentElement.lang || 'ja-JP';
    const fallbackImageUrl = section.dataset.fallbackImageUrl || '';
    const fallbackImageAlt = section.dataset.fallbackImageAlt || '';
    const price = section.querySelector('[data-combination-price]');
    const comparePrice = section.querySelector('[data-combination-compare-price]');
    const subtotal = section.querySelector('[data-combination-subtotal]');
    const status = section.querySelector('[data-combination-status]');
    const submitLabel = section.querySelector('[data-combination-submit-label]');
    const characterLabel = section.querySelector('[data-combination-character-label]');
    const decrease = section.querySelector('[data-combination-decrease]');
    const increase = section.querySelector('[data-combination-increase]');

    ['[data-combination-character]', '[data-combination-phrase]'].forEach((selector) => {
      const field = section.querySelector(selector);
      if (!field) return;
      const controls = [...field.querySelectorAll('[data-combination-option]')];
      if (controls.some((control) => selectedControlValue(control))) return;
      const firstInput = controls[0];
      if (!firstInput) return;
      if (isSelectControl(firstInput)) {
        const firstOption = [...firstInput.options].find((option) => String(option.value || '').trim() !== '');
        if (firstOption) firstInput.value = firstOption.value;
      } else {
        firstInput.checked = true;
      }
    });

    const readQuantity = () => {
      const minimum = Number(quantity.min) || 1;
      const maximum = Number(quantity.max) || 99;
      const value = Number.parseInt(quantity.value, 10);
      return Math.min(maximum, Math.max(minimum, Number.isFinite(value) ? value : minimum));
    };

    const readSelections = () => Object.fromEntries(optionInputs
      .map((input) => [input.dataset.optionPosition, selectedControlValue(input)])
      .filter(([position, value]) => position && value));

    const updateCharacterLabel = () => {
      const character = section.querySelector('[data-combination-character] [data-combination-option]:checked');
      if (character && characterLabel) {
        characterLabel.textContent = character.dataset.optionLabel || character.value;
      }
    };

    const setMainImage = (selection) => showProductImage(section, selection);

    const setActiveThumbnail = (activeThumb) => {
      if (window.AgetaProductGallery?.get(section)) return;
      section.querySelectorAll('[data-combination-image-thumb]').forEach((thumb) => {
        const current = thumb === activeThumb;
        thumb.classList.toggle('is-active', current);
        thumb.setAttribute('aria-current', String(current));
      });
    };

    const updateQuantity = (variant) => {
      const value = readQuantity();
      quantity.value = String(value);
      if (decrease) decrease.disabled = value <= (Number(quantity.min) || 1);
      if (increase) increase.disabled = value >= (Number(quantity.max) || 99);
      if (subtotal && variant) subtotal.textContent = formatMoney(variant.price * value, currency, locale);
    };

    const update = () => {
      const variant = findVariant(variants, readSelections());
      updateCharacterLabel();

      if (!variant) {
        submit.disabled = true;
        if (submitLabel) submitLabel.textContent = '組み合わせを選択してください';
        if (status) status.textContent = 'この組み合わせは選択できません。';
        updateQuantity(null);
        return;
      }

      variantSelect.value = variant.id;
      if (price) price.textContent = variant.priceFormatted;
      if (comparePrice) {
        comparePrice.textContent = variant.comparePriceFormatted;
        comparePrice.hidden = !variant.comparePriceFormatted;
      }
      setMainImage(variantImage(variant, fallbackImageUrl, fallbackImageAlt));
      setActiveThumbnail([...section.querySelectorAll('[data-combination-image-thumb]')].find((thumb) => thumb.dataset.imageUrl === variant.imageUrl));

      submit.disabled = !variant.available;
      if (submitLabel) {
        submitLabel.textContent = variant.available ? submit.dataset.availableLabel : submit.dataset.soldOutLabel;
      }
      if (status) status.textContent = variant.available ? '' : '選択した組み合わせは現在売り切れです。';
      updateQuantity(variant);
    };

    optionInputs.forEach((input) => input.addEventListener('change', update));
    quantity.addEventListener('input', update);
    quantity.addEventListener('change', update);
    decrease?.addEventListener('click', () => {
      quantity.value = String(readQuantity() - 1);
      update();
    });
    increase?.addEventListener('click', () => {
      quantity.value = String(readQuantity() + 1);
      update();
    });


    update();
  };

  const initHierarchicalProduct = (section) => {
    if (section.dataset.combinationBound === 'true') return;

    const baseVariant = section.querySelector('[data-combination-base-variant]');
    const quantity = section.querySelector('[data-combination-quantity]');
    const submit = section.querySelector('[data-combination-submit]');
    if (!baseVariant || !quantity || !submit) return;

    section.dataset.combinationBound = 'true';
    const characterInputs = [...section.querySelectorAll('[data-hierarchical-character]')];
    const detailCards = [...section.querySelectorAll('[data-hierarchical-detail-card]')];
    const detailEntries = detailCards.map((card) => ({
      card,
      characterValue: card.dataset.characterValue || '',
      input: card.querySelector('[data-hierarchical-detail]'),
    })).filter((detail) => detail.input);
    const detailInputs = detailEntries.map((detail) => detail.input);
    const phraseInputs = [...section.querySelectorAll('[data-hierarchical-phrase]')];
    const detailFieldset = section.querySelector('[data-combination-detail]');
    const phraseFieldset = section.querySelector('[data-combination-phrase]');
    const details = section.querySelector('[data-combination-details]');
    const phrases = section.querySelector('[data-combination-phrases]');
    const detailPrompt = section.querySelector('[data-combination-detail-prompt]');
    const phrasePrompt = section.querySelector('[data-combination-phrase-prompt]');
    const characterLabel = section.querySelector('[data-combination-character-label]');
    const detailLabel = section.querySelector('[data-combination-detail-label]');
    const phraseLabel = section.querySelector('[data-combination-phrase-label]');
    const characterId = section.querySelector('[data-combination-character-id]');
    const detailId = section.querySelector('[data-combination-detail-id]');
    const detailImage = section.querySelector('[data-combination-detail-image]');
    const phraseId = section.querySelector('[data-combination-phrase-id]');
    const imageThumbs = [...section.querySelectorAll('[data-combination-image-thumb]')];
    const subtotal = section.querySelector('[data-combination-subtotal]');
    const status = section.querySelector('[data-combination-status]');
    const submitLabel = section.querySelector('[data-combination-submit-label]');
    const decrease = section.querySelector('[data-combination-decrease]');
    const increase = section.querySelector('[data-combination-increase]');
    const currency = section.dataset.currency || 'JPY';
    const locale = document.documentElement.lang || 'ja-JP';
    const fallbackImageUrl = section.dataset.fallbackImageUrl || '';
    const fallbackImageAlt = section.dataset.fallbackImageAlt || '';
    const basePrice = Number(baseVariant.dataset.price);
    const available = baseVariant.dataset.available === 'true';
    const saleStartsAt = Date.parse(section.dataset.saleStartsAt || '');
    const salePending = () => Number.isFinite(saleStartsAt) && Date.now() < saleStartsAt;
    let configurationMessage = '';

    const selectedInput = (inputs) => inputs.find((input) => selectedControlValue(input));

    const readQuantity = () => {
      const minimum = Number(quantity.min) || 1;
      const maximum = Number(quantity.max) || 99;
      const value = Number.parseInt(quantity.value, 10);
      return Math.min(maximum, Math.max(minimum, Number.isFinite(value) ? value : minimum));
    };

    const setMainImage = (selection) => showProductImage(section, selection);

    const setActiveThumbnail = (activeThumb) => {
      if (window.AgetaProductGallery?.get(section)) return;
      section.querySelectorAll('[data-combination-image-thumb]').forEach((thumb) => {
        const current = thumb === activeThumb;
        thumb.classList.toggle('is-active', current);
        thumb.setAttribute('aria-current', String(current));
      });
    };

    const showSelectionImage = (input) => {
      if (!input) return;
      setMainImage({
        url: input.dataset.imageUrl || fallbackImageUrl,
        alt: input.dataset.imageAlt || fallbackImageAlt,
      });
      setActiveThumbnail(null);
    };

    const updateSelection = (label, idField, input) => {
      const selection = readSelection(input);
      if (label) {
        label.textContent = selection.label;
        label.hidden = !selection.value;
      }
      if (idField) idField.value = selection.id;
    };

    const updateQuantity = () => {
      const value = readQuantity();
      quantity.value = String(value);
      if (decrease) decrease.disabled = value <= (Number(quantity.min) || 1);
      if (increase) increase.disabled = value >= (Number(quantity.max) || 99);
      if (subtotal && Number.isFinite(basePrice)) {
        subtotal.textContent = formatMoney(basePrice * value, currency, locale);
      }
    };

    const setPhraseReady = (ready) => {
      const hasPhrases = hasSelectableOptions(phraseInputs);
      if (phraseFieldset) phraseFieldset.disabled = !ready || !hasPhrases;
      if (phrases) phrases.hidden = !ready || !hasPhrases;
      if (phrasePrompt) {
        phrasePrompt.hidden = ready && hasPhrases;
        phrasePrompt.textContent = hasPhrases
          ? '先に表情・ポーズをえらんでください。'
          : 'セリフが登録されていません。';
      }
    };

    const update = () => {
      const character = selectedInput(characterInputs);
      const detail = selectedInput(detailInputs);
      const phrase = phraseFieldset?.disabled ? undefined : selectedInput(phraseInputs);
      const characterSelection = readSelection(character);
      const detailSelection = readSelection(detail);
      const phraseSelection = readSelection(phrase);
      updateSelection(characterLabel, characterId, character);
      updateSelection(detailLabel, detailId, detail);
      updateSelection(phraseLabel, phraseId, phrase);
      if (detailImage) detailImage.value = detailSelection.value ? (detail.dataset.cartImageUrl || '') : '';

      const complete = hierarchicalSelectionComplete({
        character: characterSelection.id,
        detail: detailSelection.id,
        phrase: phraseSelection.id,
      });

      const pending = salePending();
      submit.disabled = pending || !complete || !available;
      if (submitLabel) {
        if (pending) submitLabel.textContent = submit.dataset.salePendingLabel;
        else if (!complete) submitLabel.textContent = submit.dataset.selectionRequiredLabel || '選択してください';
        else submitLabel.textContent = available ? submit.dataset.availableLabel : submit.dataset.soldOutLabel;
      }
      if (status) {
        if (configurationMessage) status.textContent = configurationMessage;
        else status.textContent = complete && !available ? 'この商品は現在売り切れです。' : '';
      }
      updateQuantity();
    };

    // Keep the launch gate when submitting with Enter as well as via the button.
    section.addEventListener('submit', (event) => {
      if (salePending()) {
        event.preventDefault();
        update();
      }
    });

    if (Number.isFinite(saleStartsAt)) {
      const refreshSaleAvailability = () => {
        if (!section.isConnected) return;
        update();
        const remaining = saleStartsAt - Date.now();
        if (remaining > 0) window.setTimeout(refreshSaleAvailability, Math.min(remaining, 60_000));
      };
      window.addEventListener('pageshow', update);
      window.addEventListener('focus', update);
      // Short waits also recover from sleeping/background tabs and clock changes.
      window.setTimeout(refreshSaleAvailability, Math.max(0, Math.min(saleStartsAt - Date.now(), 60_000)));
    }

    const selectCharacter = (character, showPreview = true) => {
      detailInputs.forEach((input) => {
        clearControl(input);
      });
      phraseInputs.forEach(clearControl);
      const matchingDetails = filterCharacterDetails(detailEntries, character.dataset.characterValue);
      detailEntries.forEach((detail) => {
        const matches = matchingDetails.includes(detail);
        detail.card.hidden = !matches;
        detail.input.disabled = !matches;
      });

      const hasDetails = matchingDetails.length > 0;
      if (detailFieldset) detailFieldset.disabled = !hasDetails;
      if (details) details.hidden = !hasDetails;
      if (detailPrompt) {
        detailPrompt.hidden = hasDetails;
        detailPrompt.textContent = hasDetails
          ? '先にキャラクターをえらんでください。'
          : 'このキャラクターの表情・ポーズが登録されていません。';
      }
      configurationMessage = hasDetails ? '' : 'このキャラクターには選べる表情・ポーズがありません。';
      setPhraseReady(false);
      if (showPreview) showSelectionImage(character);
      update();
    };

    const advanceTo = (fieldset) => {
      if (!fieldset || fieldset.disabled) return;
      fieldset.scrollIntoView({ block: 'start', behavior: window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
    };
    characterInputs.forEach((input) => input.addEventListener('change', () => {
      selectCharacter(input);
      advanceTo(detailFieldset);
    }));
    characterInputs.forEach((input) => input.addEventListener('click', () => {
      if (input.checked) advanceTo(detailFieldset);
    }));
    detailInputs.forEach((input) => input.addEventListener('change', () => {
      configurationMessage = hasSelectableOptions(phraseInputs) ? '' : 'セリフが登録されていません。';
      setPhraseReady(true);
      showSelectionImage(input);
      update();
      advanceTo(phraseFieldset);
    }));
    phraseInputs.forEach((input) => input.addEventListener('change', update));
    quantity.addEventListener('input', update);
    quantity.addEventListener('change', update);
    decrease?.addEventListener('click', () => {
      quantity.value = String(readQuantity() - 1);
      update();
    });
    increase?.addEventListener('click', () => {
      quantity.value = String(readQuantity() + 1);
      update();
    });


    detailEntries.forEach((detail) => {
      detail.card.hidden = true;
      detail.input.disabled = true;
    });
    if (detailFieldset) detailFieldset.disabled = true;
    if (details) details.hidden = true;
    setPhraseReady(false);
    if (characterInputs.length === 0) configurationMessage = 'キャラクターが登録されていません。';
    const initialCharacter = selectedInput(characterInputs);
    if (initialCharacter) {
      selectCharacter(initialCharacter, !section.hasAttribute('data-campaign-product'));
      if (section.hasAttribute('data-campaign-product') && imageThumbs[0]) imageThumbs[0].click();
    }
    else update();
  };

  const boundGalleries = new WeakSet();
  const bindGallery = (section) => {
    if (boundGalleries.has(section)) return;
    boundGalleries.add(section);
    if (typeof window !== 'undefined') {
      if (window.requestIdleCallback) window.requestIdleCallback(() => preloadGallery(section), { timeout: 1000 });
      else window.setTimeout?.(() => preloadGallery(section), 0);
    }
    // Delegate to the section so replacing gallery children keeps clicks working.
    section.addEventListener('click', (event) => {
      const thumb = event.target.closest?.('[data-combination-image-thumb]');
      if (!thumb || thumb.closest('[data-combination-product]') !== section) return;
      if (!thumb.dataset.imageUrl || window.AgetaProductGallery?.get(section)) return;
      showProductImage(section, { url: thumb.dataset.imageUrl, alt: thumb.dataset.imageAlt });
      section.querySelectorAll('[data-combination-image-thumb]').forEach((item) => {
        item.classList.toggle('is-active', item === thumb);
        item.setAttribute('aria-current', String(item === thumb));
      });
    });
  };

  const initProduct = (section) => {
    bindGallery(section);
    if (section.dataset.selectionMode === 'hierarchical') initHierarchicalProduct(section);
    else initVariantProduct(section);
  };

  const init = (root = document) => {
    if (root.matches?.('[data-combination-product]')) initProduct(root);
    root.querySelectorAll('[data-combination-product]').forEach(initProduct);
  };

  if (typeof window !== 'undefined') {
    window.AgetaCombinationProduct = {
      filterCharacterDetails,
      findVariant,
      formatMoney,
      getClearedControlState,
      hasSelectableOptions,
      hierarchicalSelectionComplete,
      readSelection,
      variantImage,
      showProductImage,
    };
  }
  if (typeof document !== 'undefined') {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
    else init();
    document.addEventListener('shopify:section:load', (event) => init(event.target));
  }
})();
